import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { NotFoundError, useFile } from "@/api/queries";
import { useAuth } from "@/auth/stub";
import { AuthGate } from "@/components/AuthGate";
import { CommentsThread } from "@/components/CommentsThread";
import { FileNameLink } from "@/components/FileNameLink";

// Where comment notifications lead (``/comments/<file>#comment-<id>``):
// the file's name (opens it) and all of its comments.
function CommentsPage({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const file = useFile(fileId);
  const selfId = useAuth((s) => s.user?.id);
  if (file.error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {file.error instanceof NotFoundError
          ? t("comments.file_gone")
          : t("comments.load_failed")}
      </p>
    );
  }
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="space-y-1">
        <h1 className="break-words text-xl font-semibold">
          {file.data ? (
            <FileNameLink file={file.data} className="hover:underline" />
          ) : (
            "…"
          )}
        </h1>
        {/* The folder above someone else's file may not be shared. */}
        {file.data &&
          (file.data.owner_id === selfId && file.data.parent_id ? (
            <Link
              to="/folder/$folderId"
              params={{ folderId: file.data.parent_id }}
              className="text-sm text-muted-foreground hover:underline"
            >
              {t("comments.open_folder")}
            </Link>
          ) : file.data.owner_id !== selfId ? (
            <Link
              to="/shared-with-me"
              className="text-sm text-muted-foreground hover:underline"
            >
              {t("nav.shared_with_me")}
            </Link>
          ) : null)}
      </div>
      <CommentsThread fileId={fileId} />
    </div>
  );
}

export const Route = createFileRoute("/comments/$fileId")({
  component: function CommentsRoute() {
    const { fileId } = Route.useParams();
    return (
      <AuthGate>
        <CommentsPage fileId={fileId} />
      </AuthGate>
    );
  },
});
